import { useTranslation } from 'next-i18next';

import { useEffect, useMemo, useState } from 'react';

import {
    Box,
    Button,
    Heading,
    Link,
    Separator,
    Spinner,
    useBreakpointValue,
} from '@chakra-ui/react';

import { RegexConst } from '@nocowanie/core';
import { AdaptiveDrawer, useLineCount } from '@nocowanie/common-ui';

import { AppModalsEnum } from '@app/enums';
import { useDrawerState, useModalFragmentSync } from '@app/hooks';
import {
    accommodationStaticSlice,
    commonUiStateSlice,
    useAppDispatch,
    useAppSelector,
} from '@app/state';

export const Description = () => {
    const { description } = useAppSelector(accommodationStaticSlice.selectors.selectDescription);
    const isMobile = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'description',
    });
    const dispatch = useAppDispatch();

    const isDrawerOpen = useAppSelector(
        commonUiStateSlice.selectors.selectModalData<boolean>(AppModalsEnum.Description),
    );

    const [showFull, setShowFull] = useState<boolean>(false);
    const [showExpandButton, setShowExpandButton] = useState<boolean>(true);

    const { isDrawerPending, openDrawer } = useDrawerState(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.Description,
                modalData: true,
            }),
        );
    });

    const closeDrawer = () => {
        dispatch(commonUiStateSlice.actions.resetModalDataAction(AppModalsEnum.Description));
    };

    const sanitizedDescription = useMemo(() => {
        const phoneRegex = RegexConst.PHONE_NUMBER_EXTENDED;
        return description?.replace(phoneRegex, t('ownerContactUnavailable'));
    }, [description]);

    const defaultLineClamp = useBreakpointValue({
        base: 10,
        md: 7,
    });
    const lineHook = useLineCount<HTMLDivElement>();

    useEffect(() => {
        if (lineHook.lineCount > (defaultLineClamp || 0)) {
            setShowExpandButton(true);
        } else {
            setShowExpandButton(false);
        }
    }, [defaultLineClamp, lineHook]);

    useModalFragmentSync(AppModalsEnum.Description, isDrawerOpen);

    if (!description) return null;

    const descriptionContent = (
        <Box
            dangerouslySetInnerHTML={{
                __html: sanitizedDescription ?? '',
            }}
            lineClamp={showFull || !showExpandButton ? undefined : defaultLineClamp}
            ref={lineHook.ref}
        />
    );

    return (
        <>
            <Box>
                <Heading size={'md'} mb={2}>
                    {t('descriptionHeader')}
                </Heading>
                {!isMobile ? (
                    <Box>
                        {showExpandButton ? (
                            <>
                                <Box minHeight={showFull ? 'auto' : '168px'}>
                                    {descriptionContent}
                                </Box>
                                <Link
                                    variant="plain"
                                    colorPalette="secondary"
                                    mt={2}
                                    onClick={() => setShowFull(!showFull)}
                                    as={'button'}
                                >
                                    {t(showFull ? 'hideDescriptionBtn' : 'showDescriptionBtn')}
                                </Link>
                            </>
                        ) : (
                            descriptionContent
                        )}
                    </Box>
                ) : (
                    <Box>
                        {descriptionContent}
                        {showExpandButton ? (
                            <>
                                <Button
                                    variant="link"
                                    colorPalette="secondary"
                                    mt={2}
                                    onClick={openDrawer}
                                    disabled={isDrawerPending}
                                >
                                    {t('showDescriptionBtn')}
                                    {isDrawerPending ? <Spinner ml={2} size={'xs'} /> : null}
                                </Button>
                                <AdaptiveDrawer
                                    header={t('descriptionHeader')}
                                    isOpen={isDrawerOpen}
                                    onOpen={openDrawer}
                                    onClose={closeDrawer}
                                    p={0}
                                    mb={24}
                                    drawerProps={{
                                        placement: 'bottom',
                                        variant: 'contentHeight',
                                    }}
                                >
                                    <Box
                                        pt={4}
                                        px={6}
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizedDescription ?? '',
                                        }}
                                    />
                                </AdaptiveDrawer>
                            </>
                        ) : null}
                    </Box>
                )}
            </Box>
            <Separator mt={5} />
        </>
    );
};
